<template>
  <div class="parent-wrapper">
    <p v-if="!showReviewSection" class="black--text mt-3 font-weight-bold">{{ $t("taxForm.w-9.formCaption") }}</p>
    <validation-observer slim ref="observer" v-slot="{ invalid, handleSubmit }">
      <v-form v-if="!showReviewSection">
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.nameEntity") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="w9Form.legalName"
              :class="{ 'has-hint': showHintvalue }"
              :placeholder="$t('taxForm.captions.enterFullName')"
              :label="$t('taxForm.captions.enterFullName')"
              :hint="namespacingHint"
              id="fullname"
              rules="required|name"
              :multiLang="false"
              persistent-hint
              :maxLength="255"
              @input="hasSpacingIrregularities"
              @blur="saveIfValid"
            >
            </VTextFieldWithValidation>
          </v-col>
          <v-col cols="12" md="6">
            <VTaxEntitySelector
              v-model="w9Form.entityType"
              rules="required"
              attach
              placeholder="selectEntity"
              label="selectEntity"
              @blur="saveIfValid"
            >
            </VTaxEntitySelector>
          </v-col>
          <v-col
            v-if="showOtherEntity || showTaxClassfication"
            cols="12"
            md="6"
            :class="{ 'd-flex align-center': showOwnerShipCheckbox }"
            @blur="saveIfValid"
          >
            <VTextFieldWithValidation
              v-if="showOtherEntity"
              v-model.trim="w9Form.entityTypeOther"
              placeholder="otherEntity"
              label="otherEntity"
              rules="required"
              :maxLength="100"
              @blur="saveIfValid"
            ></VTextFieldWithValidation>
            <VTaxClassificationSelector
              v-if="showTaxClassfication"
              attach
              v-model="w9Form.taxClarification"
              placeholder="selectTax"
              label="selectTax"
            >
            </VTaxClassificationSelector>
          </v-col>
          <!-- When entity type is Trust/Estate or Partnership -->
          <v-col v-if="showOwnerShipCheckbox || isFullWidthEntity" cols="12" md="12">
            <v-checkbox
              @change="saveIfValid"
              v-model="w9Form.ownershipInterest"
              color="primary"
              class="black--text black--text custom-label"
              :label="$t('messages.ownershipContent')"
            />
          </v-col>
          <!-- When entity type is anything else -->
          <v-col v-else-if="showOwnerShipCheckbox" cols="12" md="6">
            <v-checkbox
              @change="saveIfValid"
              v-model="w9Form.ownershipInterest"
              color="primary"
              class="black--text black--text custom-label"
              :label="$t('messages.ownershipContent')"
              @blur="saveIfValid"
            />
          </v-col>
        </v-row>
        <v-divider class="my-6"></v-divider>
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.permanentAddress") }}</h3>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="w9Form.address"
              clearable
              placeholder="address"
              label="address"
              rules="required"
              :maxLength="255"
              @blur="saveIfValid"
            ></VTextFieldWithValidation>
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              placeholder="city"
              clearable
              v-model.trim="w9Form.city"
              label="city"
              :maxLength="100"
              @blur="saveIfValid"
            ></VTextFieldWithValidation>
          </v-col>
          <v-col cols="12" md="6">
            <v-country-states-selector-with-validation
              v-model="w9Form.stateId"
              attach
              @state-selected="currentState"
              clearable
              rules="required"
              id="state"
              name="state"
              placeholder="selectStateTerritory"
              :showUsStates="true"
              :countryId="countryId"
              label="selectStateTerritory"
              @blur="saveIfValid"
            />
          </v-col>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="w9Form.zipCode"
              placeholder="enterZipCode"
              label="enterZipCode"
              clearable
              :readonly="true"
              @focus="removeReadOnly"
              rules="required"
              :maxLength="10"
              @blur="saveIfValid"
            >
            </VTextFieldWithValidation>
          </v-col>
        </v-row>
        <v-divider class="my-6"></v-divider>
        <v-row>
          <v-col cols="12" class="pb-0">
            <h3 class="primary--text">{{ $t("taxForm.captions.identificationNumberw9") }}</h3>
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              v-if="hasSavedTin && w9Form.ssn && !editingTin"
              :value="maskedSsn"
              label="Social Security Number"
              readonly
              class="masked-tin-field"
              append-icon="$edit"
              @click:append="editTin"
            />
            <VTextFieldWithValidation
              v-else
              v-model="w9Form.ssn"
              name="securityNumber"
              rules="ssnFormat"
              label="securityNumber"
              placeholder="securityNumber"
              :append-icon="showSecurityNumber ? '$eye' : '$eyeSlash'"
              :type="showSecurityNumber ? 'text' : 'password'"
              @input="formatSsn"
              @click:append="showSecurityNumber = !showSecurityNumber"
              clearable
              :maxLength="11"
              @blur="saveIfValid"
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center justify-center"
            ><span class="primary--text text-center d-block">OR</span></v-col
          >
          <v-col cols="12" md="5">
            <v-text-field
              v-if="hasSavedTin && w9Form.ein && !editingTin"
              :value="maskedEin"
              label="Employer Identification Number"
              readonly
              class="masked-tin-field"
              append-icon="$edit"
              @click:append="editTin"
            />
            <VTextFieldWithValidation
              v-else
              v-model="w9Form.ein"
              name="employerIdentification"
              rules="einFormat"
              label="employerIdentification"
              placeholder="employerIdentification"
              clearable
              :append-icon="showIdentificationNumber ? '$eye' : '$eyeSlash'"
              :type="showIdentificationNumber ? 'text' : 'password'"
              autocomplete="off"
              inputmode="numeric"
              @input="formatEin"
              @click:append="showIdentificationNumber = !showIdentificationNumber"
              :readonly="true"
              @focus="removeReadOnly"
              :maxLength="10"
              @blur="saveIfValid"
            />
          </v-col>
        </v-row>
        <v-row v-if="tinWarning" class="mt-1" no-gutters>
          <v-col class="d-flex justify-center">
            <div class="error--text text-caption pb-4">
              {{ tinWarning }}
            </div>
          </v-col>
        </v-row>
        <h3 class="primary--text">{{ $t("taxForm.captions.reviewCertification") }}</h3>
        <p class="black--text font-weight-bold">{{ $t("taxForm.captions.reviewCertificationContent") }}</p>
        <div class="text-center">
          <v-btn color="primary" type="button" :disabled="invalid || !hasTin" @click="showReviewSection = true"
            >{{ $t("taxForm.captions.review") }}
          </v-btn>
        </div>
      </v-form>
      <div v-else>
        <p class="black--text font-weight-bold pt-3">Review your W-9 form data and certify to submit to our database</p>
        <v-row class="pb-3">
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              {{ $t("taxForm.captions.fullName") }}
              <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ w9Form.legalName }}</p>
          </v-col>
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              {{ $t("fields.cfDefinitionEntityType") }}
              <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ w9Form.entityType }}</p>
          </v-col>
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              {{ $t("account.titles.address") }}
              <v-icon color="primary" class="cursor-pointer ms-1" small @click="showReviewSection = false"
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">{{ w9Form.address }}</p>
            <p class="black--text mb-0">{{ w9Form.city }} {{ w9Form.state }} {{ w9Form.zipCode }}</p>
          </v-col>
          <v-col cols="12" md="6">
            <p class="font-weight-bold black--text mb-0">
              {{ $t("taxForm.captions.identificationNumberw9") }}
              <v-icon
                @click="
                  showReviewSection = false;
                  editTin();
                "
                color="primary"
                class="cursor-pointer ms-1"
                small
                >$edit
              </v-icon>
            </p>
            <p class="black--text mb-0">
              {{ !showTin ? showMaskValue : showTaxIdentificationNumber }}
              <v-icon class="ms-2" small @click="showTin = !showTin">{{ !showTin ? "$eye" : "$eyeSlash" }}</v-icon>
            </p>
          </v-col>
        </v-row>
        <v-divider></v-divider>
        <p class="black--text font-weight-bold my-3">{{ $t("taxForm.typingName") }}</p>
        <v-list class="ps-0 certification-text-wrapper">
          <v-list-group v-model="openCertificateSection">
            <template v-slot:activator>
              <v-list-item-title class="primary--text font-weight-bold"
                >{{ openCertificateSection ? "Hide Certification Text" : "View Certification Text" }}
              </v-list-item-title>
            </template>
            <v-list-item class="px-0">
              <div v-html="$t('taxForm.certificateContent')"></div>
            </v-list-item>
          </v-list-group>
        </v-list>
        <v-row>
          <v-col cols="12" md="6">
            <VTextFieldWithValidation
              v-model.trim="certificateName"
              :placeholder="$t('taxForm.captions.enterFullName')"
              :label="$t('taxForm.captions.enterFullName')"
              rules="required"
              :class="{ 'has-hint': hasCertificateNameHint }"
              :hint="testCertifyName"
              :multiLang="false"
              persistent-hint
              :maxLength="255"
              autocomplete="off"
            />
          </v-col>

          <v-col cols="12" md="6" class="d-flex justify-end align-center" :class="{ 'justify-center': isMobile }">
            <v-btn
              color="primary"
              type="submit"
              :loading="submitLoading"
              :disabled="!certificateName || invalidNameMatch"
              @click="submitCertificate()"
              >Certify
            </v-btn>
          </v-col>
        </v-row>
      </div>
      <v-overlay absolute :value="overlayLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </validation-observer>
  </div>
</template>
<script lang="ts">
import Vue, { PropType, VueConstructor } from "vue";
import VTextFieldWithValidation from "../VTextFieldWithValidation.vue";
import { ValidationObserver } from "vee-validate";
import ErrorLabel from "@/components/objects/ErrorLabel.vue";
import VTaxEntitySelector from "../VTaxEntitySelector.vue";
import { UserPartialItem, UserService, UserTaxInformationRecordItem } from "@/service";
import VTaxClassificationSelector from "../VTaxClassificationSelector.vue";
import VCountryStatesSelectorWithValidation from "../VCountryStatesSelectorWithValidation.vue";
import { DateTime } from "luxon";
import { UserTaxEntityTypeEnum } from "@/service";

import { RootStoreInterface } from "@/store/types";
import { createHelpers } from "vuex-typescript-interface";

const { mapGetters, mapActions } = createHelpers<RootStoreInterface>();
const getters = mapGetters(["user"]);

const actions = mapActions(["updateTaxFormStatus"]);

export default (
  Vue as VueConstructor<
    Vue & {
    saveIfValid: () => Promise<void>;
    $refs: {
        observer: InstanceType<typeof ValidationObserver>;
        errorLabel: InstanceType<typeof ErrorLabel>;
      };
    }
  >
).extend({
  components: {
    ValidationObserver,
    VTextFieldWithValidation,
    VTaxEntitySelector,
    VTaxClassificationSelector,
    VCountryStatesSelectorWithValidation,
  },
  data: () => ({
    isInitializing: false,
    submitLoading: false,
    overlayLoading: false,
    showTin: false,
    entityType: "" as UserTaxEntityTypeEnum,
    taxClarification: "" as UserTaxInformationRecordItem.taxClarification,
    showOtherEntity: false,
    showTaxClassfication: false,
    showOwnerShipCheckbox: false,
    countryId: 233, // USA
    certificateName: "",
    showSecurityNumber: false,
    showIdentificationNumber: false,
    loading: false,
    openCertificateSection: false,
    recordId: undefined as number | undefined,
    showReviewSection: false,
    fetchRunning: false,
    namespacingHint: "",
    showHintvalue: false,
    hasCertificateNameHint: false,
    editingTin: false,
    hasSavedTin: false,
    w9Form: {
      id: undefined as number | undefined,
      legalName: "",
      formType: UserTaxInformationRecordItem.formType.W_9_FORM,
      ownershipInterest: false,
      entityType: "" as UserTaxEntityTypeEnum,
      taxClarification: "" as UserTaxInformationRecordItem.taxClarification,
      stateId: undefined as number | undefined,
      securityNumber: "",
      identificationNumber: "",
      city: "",
      state: "",
      entityTypeOther: "",
      address: "",
      ssn: "",
      ein: "",
      zipCode: undefined as number | undefined,
    } as UserTaxInformationRecordItem,
  }),
  props: {
    prefilledRes: {
      type: Object as PropType<UserTaxInformationRecordItem>,
      default: () => ({}) as UserTaxInformationRecordItem,
    },
    showW9Form: {
      type: Boolean,
      default: false,
    },
    parentLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...getters,
    userId(): number {
      return this.user?.id || 0;
    },
    showMaskValue(): string {
      if (this.w9Form.ssn) return "***-**-****";
      if (this.w9Form.ein) return "**-*******";
      return "";
    },
    invalidNameMatch() {
      let legalName = this.normalizeName(this.w9Form.legalName);
      let certificateName = this.normalizeName(this.certificateName);
      return legalName != certificateName;
    },
    testCertifyName() {
      if (this.certificateName && this.invalidNameMatch) {
        this.$nextTick(() => {
          this.hasCertificateNameHint = true;
        });
        return this.$t("taxForm.validation.confirmed");
      }
      this.$nextTick(() => {
        this.hasCertificateNameHint = false;
      });
      return "";
    },
    showHint() {
      return this.certificateName && this.invalidNameMatch;
    },
    showTaxIdentificationNumber(): string {
      if (this.w9Form.ssn) {
        const lastFour = this.w9Form.ssn.toString().replace(/\D/g, "").slice(-4);
        return lastFour ? `***-**-${lastFour}` : "";
      }
      if (this.w9Form.ein) {
        const lastFour = this.w9Form.ein.toString().replace(/\D/g, "").slice(-4);
        return lastFour ? `**-***${lastFour}` : "";
      }
      return "";
    },
    hasTaxidentificationNumber() {
      return this.w9Form?.ssn?.length || this.w9Form?.ein?.length;
    },
    isMobile(): boolean {
      return this.$vuetify.breakpoint.smAndDown;
    },
    taxFormNotStarted(): boolean {
      return (
        this.user?.taxFormStatus == UserPartialItem.taxFormStatus.NOT_STARTED ||
        this.user?.taxFormStatus == UserPartialItem.taxFormStatus.IN_PROGRESS
      );
    },
    isFullWidthEntity(): boolean {
      const entity = this.w9Form.entityType;
      return [
        UserTaxEntityTypeEnum.TRUST_ESTATE,
        UserTaxEntityTypeEnum.PARTNERSHIP,
      ].includes(entity);
    },
    ssnRules(): string {
      if (this.w9Form.ssn) return "ssnFormat";
      return this.w9Form.ein ? "" : "required";
    },
    einRules(): string {
      if (this.w9Form.ein) return "einFormat";
      return this.w9Form.ssn ? "" : "required";
    },
    maskedSsn(): string {
      const lastFour = (this.w9Form.ssn || "").replace(/\D/g, "").slice(-4);
      return lastFour ? `***-**-${lastFour}` : "";
    },
    maskedEin(): string {
      const lastFour = (this.w9Form.ein || "").replace(/\D/g, "").slice(-4);
      return lastFour ? `**-***${lastFour}` : "";
    },
    tinWarning(): string {
      return !this.w9Form.ssn && !this.w9Form.ein
        ? (this.$t("customValidations.messages.tinWarningW9") as string)
        : "";
    },
    hasTin(): boolean {
      return !!(this.w9Form.ssn || this.w9Form.ein);
    },
  },
  methods: {
    ...actions,
    updateFormData(response: UserTaxInformationRecordItem) {
      this.isInitializing = true;
      (this.w9Form.countryId = 233), // USA
        (this.w9Form.id = response?.id ? response.id : undefined);
      this.w9Form.legalName = response?.legalName ? response.legalName : "";
      this.w9Form.entityType = response?.entityType ? response.entityType : undefined;
      this.w9Form.taxClarification = response?.taxClarification ? response.taxClarification : undefined;
      this.w9Form.stateId = response?.stateId ? response.stateId : undefined;
      this.w9Form.city = response?.city ? response.city : "";
      this.w9Form.entityTypeOther = response?.entityTypeOther ? response.entityTypeOther : undefined;
      this.w9Form.address = response?.address ? response.address : "";
      this.w9Form.state = response?.state ? response.state : "";
      this.w9Form.zipCode = response?.zipCode ? response.zipCode : "";
      this.w9Form.ssn = response?.ssn ? response.ssn : "";
      this.w9Form.ein = response?.ein ? response.ein : "";
      this.hasSavedTin = !!(response?.ssn || response?.ein);
      this.editingTin = false;
      this.w9Form.ownershipInterest = response.ownershipInterest ? true : false;

      this.$nextTick(() => {
        this.isInitializing = false;
      });
    },
    async createFromData() {
      this.loading = true;
      this.fetchRunning = true;
      const payload = this.getFilterPayload();
      const id = this.userId;
      try {
        const res = await UserService.createTaxInformation({ id: id as number, requestBody: payload });
        if (res) this.w9Form.id = res.id;
      } catch (e) {
        console.log("error", e);
      }
      this.fetchRunning = false;
      this.loading = false;
    },
    updateTaxFrom() {
      this.loading = true;
      this.fetchRunning = true;

      const payload = this.getFilterPayload();

      // remove certify date on edits
      payload.certifyDate = null;

      const id = this.userId as number;
      try {
        UserService.updateTaxInformation({ id: id, requestBody: payload });
      } catch (e) {
        console.log("error", e);
      }
      this.loading = false;
      this.fetchRunning = false;
    },
    saveFormData() {
      if (this.isInitializing) return;
      if (this.fetchRunning) return;
      if (this.parentLoading) return;

      if (!this.w9Form.id) {
        this.createFromData();
      } else {
        this.updateTaxFrom();
      }
    },
    async submitCertificate() {
      if (!this.hasTin) return;
      this.submitLoading = true;
      if (!this.w9Form.certificationDate)
        this.w9Form.certificationDate = this.formatDateTimeRequirement(Math.floor(Date.now() / 1000));
      const payload = this.w9Form;
      const id = this.userId as number;
      try {
        await UserService.updateTaxInformation({ id: id, requestBody: payload });

        const userRes = await UserService.getUserById({ id: id });

        // update tax form status in global storage: local storage and FE user partial item
        if (this.taxFormNotStarted) this.updateTaxFormStatus(userRes.taxFormStatus);

        this.$emit("submit-modal");
        this.$confirm(
          `Your W-9 Form has been successfully submitted. Your information has been stored and your next payment will be issued per our standard payment processing timelines.`,
          {
            icon: "",
            title: `W-9 Form Submitted`,
            buttonTrueText: "",
            buttonFalseText: "",
          },
        );
      } catch (e) {
        console.log("error", e);
      }
      this.submitLoading = false;
    },
    formatDateTimeRequirement(timestamp: number) {
      let d = DateTime.fromSeconds(timestamp);
      return d.toFormat("yyyy-MM-dd");
    },
    currentState(val: any) {
      if (!val) this.w9Form.state = "";
      this.w9Form.state = val.text;
    },
    removeReadOnly(event: any) {
      event.target.removeAttribute("readonly");
    },
    normalizeName(str: string, keepOriginalLetters?: boolean) {
      let sanitizeNameSpace = str?.trim().replace(/\s+/g, " ");
      return !keepOriginalLetters ? sanitizeNameSpace.toLowerCase() : sanitizeNameSpace;
    },

    getFilterPayload() {
      this.w9Form.countryId = undefined;

      let sanitizePayload = JSON.parse(JSON.stringify(this.w9Form)); //individual object to not trigger API call twice on field modification
      sanitizePayload.legalName = this.normalizeName(sanitizePayload.legalName, true);
      return sanitizePayload;
    },
    hasSpacingIrregularities() {
      let name = this.w9Form.legalName ?? "";
      const spacingRegex = /^\s|\s{2,}|\s$/;

      let hasSpacing = spacingRegex.test(name);
      this.namespacingHint = hasSpacing ? (this.$t("hints.unnecessarySpaces") as string) : "";

      this.$nextTick(() => {
        this.showHintvalue = hasSpacing;
      });
    },
    //Format SSN to XXX-XX-XXXX
    formatSsn() {
      this.$nextTick(() => {
        const digits = (this.w9Form.ssn || "").replace(/\D/g, "").slice(0, 9);
        this.w9Form.ssn =
          digits.length <= 3
            ? digits
            : digits.length <= 5
              ? `${digits.slice(0, 3)}-${digits.slice(3)}`
              : `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      });
    },
    //Format EIN to XX-XXXXXXX
    formatEin() {
      this.$nextTick(() => {
        const digits = (this.w9Form.ein || "").replace(/\D/g, "").slice(0, 9);
        this.w9Form.ein =
          digits.length <= 2
            ? digits
            : `${digits.slice(0, 2)}-${digits.slice(2)}`;
      });
    },
    editTin() {
      this.editingTin = true;
      this.hasSavedTin = false;
      this.showSecurityNumber = false;
      this.showIdentificationNumber = false;
      this.showTin = false;
      this.w9Form.ssn = "";
      this.w9Form.ein = "";
    },
    async saveIfValid() {
      if (this.isInitializing || this.fetchRunning || this.parentLoading) return;
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      this.saveFormData();
    },
  },
  watch: {
    "w9Form.entityType"(newVal) {
      this.showOtherEntity = newVal === UserTaxEntityTypeEnum.OTHER;
      this.showTaxClassfication = newVal === UserTaxEntityTypeEnum.LLC;
      this.showOwnerShipCheckbox = [
        UserTaxEntityTypeEnum.TRUST_ESTATE,
        UserTaxEntityTypeEnum.PARTNERSHIP,
      ].includes(newVal);
    },
    "w9Form.taxClarification"(newVal) {
      this.showOwnerShipCheckbox = newVal === UserTaxInformationRecordItem.taxClarification.PARTNERSHIP;
    },
    showW9Form: {
      handler(newVal) {
        if (newVal) this.updateFormData(this.prefilledRes);
      },
      immediate: true,
    },
    prefilledRes(newVal) {
      if (newVal) {
        this.updateFormData(newVal);
      }
    },
  },
  mounted() {
    this.updateFormData(this.prefilledRes);
  },
  // mounted() {
  //     // this.fetchFormData();
  // }
});
</script>
<style lang="scss" scoped>
:deep(.v-list),
.v-list {
  background-color: white !important;
}

:deep(.v-list-item) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.parent-wrapper {
  position: relative;
}

:deep(.v-input__control .v-messages__message) {
  color: #f44336 !important;
}

.certification-text-wrapper :deep(.v-list-item) {
  padding: 0;
  }
  
.has-hint :deep(.v-input__control .v-messages__message) {
  color: rgba(0, 0, 0, 0.6) !important;
}
.masked-tin-field {
  :deep(.v-input__slot) {
    background: #f5f5f5;
  }
  :deep(input) {
    color: rgba(0, 0, 0, 0.6);
  }
}
</style>
